<template>
    <DetailCard
      v-if="!isProductDetailLoading"
      key="urun-aciklamasi"
      cardKey="product-description"
      title="Ürün Açıklaması"
      :showUpdateButton="!descriptionEditable"
      @requestUpdate="handleRequestUpdate"
      :showApplyToVariants="descriptionEditable && !isProductDetailLoading"
      @saveChanges="handleSaveChanges"
      v-model:applyToVariants="applyToVariants"
      :saveButtonText="saveButtonText"
      :isLoading="isProductDetailLoading"
      :isSaved="!isDescriptionReadyToSave || !isDirty"
      :isSaving="isSaving"
      :showSuccessState="showSuccessState"
      :saveButtonWidth="showSuccessState ? 'auto' : '183px'"
    >

      <div :class="{ 'description-content': !descriptionEditable}">
        <RichText
          v-if="descriptionEditable"
          ref="richText"
          :content="richTextContent"
          :isHasContent="descriptionEditable"
          :disabled="isProductDisabled"
          @open-modal="openSelectedTempModal"
          @update:content="editorContent = $event"
          v-model:isReusablePattern="isReusablePattern"
          v-model:templateName="templateName"
        />

        <HtmlDisplay
          v-else-if="internalContent"
          :content="internalContent"
        />

        <div v-else class="placeholder">
          {{ placeholder }}
        </div>
      </div>

    <div class="info-message" :class="{ disabled: isProductDisabled }" v-if="!descriptionEditable">
      <Icons name="InfoCircle" :color="isProductDisabled ? '#C7C7C7' : '#4FA4E5'" lg class="info-icon" />
      <span class="info-text">
        Bu açıklama n11 kataloğunda olduğu için düzenleme yapamıyorsunuz.
        İçerikte hata olduğunu düşünüyorsanız,
        <span class="highlight-text" @click="openRequestUpdateModal">Güncelleme Talep Et/Hata Bildir</span>
        ile talebinizi iletebilirsiniz.
      </span>
    </div>
  </DetailCard>
  <SelectDescriptionTempModal
    v-if="!isProductDetailLoading"
    ref="selectDescriptionTempModalRef"
    :templates="templates"
    @template-applied="applyTemplateToEditor"
  />
  <RequestUpdateModalForProductDescription
    v-if="!isProductDetailLoading"
    ref="requestUpdateModalRef"
  />
  <ProductDescriptionSkeleton  v-else />
</template>

<script setup>
import { useStore } from 'vuex';
import { ref, watch, onMounted, computed, nextTick } from "vue";
import DetailCard from "./DetailCard.vue";
import { Icons } from "@n11/so-ui-components";
import { sendProductDetailEvent } from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';
import HtmlDisplay from "./HtmlDisplay.vue"; //ortaklastiralacak singleproduct daki component ile
import RichText from './RichText';
import SelectDescriptionTempModal from './SelectDescriptionTempModal.vue';
import ProductDescriptionSkeleton from '@/components/Skeleton/ProductDetailSkeleton/ProductDescriptionSkeleton.vue'
import RequestUpdateModalForProductDescription from './Modals/RequestUpdateModalForProductDescription.vue';

const emit = defineEmits(["requestUpdate", "hasChanges", "saved"]);
const store = useStore();
const templates = ref([]);

const productDetail = computed(() => store.getters['products/getProductDetail']);
const isProductDetailLoading = computed(() => store.getters['products/isProductDetailLoading']);

const props = defineProps({
  content: {
    type: String,
    required: false,
  },
  isHasContent: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  descriptionEditable: {
    type: Boolean,
    default: false
  },
});

const isProductDisabled = computed(() => store.getters['products/isProductDisabled']);

const isDescriptionReadyToSave = ref(false);
const internalContent = ref(props.content || null);
const placeholder = "Ürün açıklaması yükleniyor...";
const selectDescriptionTempModalRef = ref(null);
const requestUpdateModalRef = ref(null);
const richText = ref(null);
const editorContent = ref(null);
const richTextContent = computed(() => editorContent.value ?? internalContent.value);
const applyToVariants = ref(true);
const isReusablePattern = ref(false);
const templateName = ref("");
const isSaved = ref(false);
const showSuccessState = ref(false);
const isSaving = computed(() => {
  return store.getters['products/isProductDescriptionLoading'];
});
const isDirty = ref(false);

watch(isSaved, (newVal) => {
  if (newVal) {
    isSaved.value = false;
  }
});

const saveButtonText = computed(() => {
  return isSaved.value ? "Değişiklikler Kaydedildi" : "Değişiklikleri Kaydet";
});

watch(
  () => props.content,
  (newVal) => {
    internalContent.value = newVal;
    if (!editorContent.value) return;
    if (normalizeForCompare(internalContent.value) === normalizeForCompare(editorContent.value)) {
      isDirty.value = false;
      isDescriptionReadyToSave.value = false;
    }
  }
);

const isHTML = (str) => {
  const doc = new DOMParser().parseFromString(str, "text/html");
  return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
}

const formatToHtml = (val) => {
  if (!val) return "";
  if (!isHTML(val.trim())) {
    return `<p>${val.trim()}</p>`;
  }
  return val.trim();
};

const normalizeForCompare = (val) => {
  return formatToHtml(val)
    .replace(/\s/g, '')
    .replace(/&nbsp;/g, ' ');
};

watch(
  editorContent,
  (newVal, oldVal) => {
    if (oldVal === null || oldVal === undefined) {
      return;
    }
    
    if(newVal !== oldVal){
      const normalizedInternal = formatToHtml(internalContent.value);
      const normalizedNewVal = formatToHtml(newVal);
      const cleanInternal = normalizedInternal.replace(/\s/g, '').replace(/&nbsp;/g, ' ');
      const cleanNewVal = normalizedNewVal.replace(/\s/g, '').replace(/&nbsp;/g, ' ');
      if (cleanInternal === cleanNewVal) {
        isDirty.value = false;
      } else {
        isDirty.value = true;
      }
      if (isDirty.value) {
        emit('hasChanges');
      }
      showSuccessState.value = false;
      isDescriptionReadyToSave.value = newVal !== oldVal;
    }
    else {
      isDirty.value = false;
    }
  }
);
watch(isSaving, (newValue, oldValue) => {
  if (oldValue === true && newValue === false) {
    const error = store.getters['products/getProductDescriptionError'];
    if (!error) {
      isDescriptionReadyToSave.value=false;
      showSuccessState.value = true;
    }
  }
});
const applyTemplateToEditor = async (templateDescription) => {
  editorContent.value = null;
  internalContent.value = null;
  
  await nextTick();
  
  editorContent.value = templateDescription.description;
  if (templateDescription.name) {
    templateName.value = templateDescription.name;
    isReusablePattern.value = true;
  }
  
  isDirty.value = true;
  isDescriptionReadyToSave.value = true;
  showSuccessState.value = false;
};

const openSelectedTempModal = async () => {
  if (templates.value.length === 0) {
    await loadDescriptionTemplates(true);
  }
  selectDescriptionTempModalRef.value?.openModal();
};

const resetTemplateData = () => {
  templateName.value = "";
  isReusablePattern.value = false;
  richText.value?.resetTemplate();
};

const handleSaveChanges = async () => {
  const productId = productDetail.value?.id;
  const description = editorContent.value || internalContent.value || '';

  if (richText.value && !richText.value.getRichText().isValid) {
    return;
  }

  const payload = {
    description,
    changesAll: applyToVariants.value,
    ...(isReusablePattern.value && templateName.value?.trim() && {
      templateName: templateName.value
    }),
  };

  try {
    await store.dispatch('products/submitDetailDescription', {
      productId,
      payload,
    });

    sendProductDetailEvent(GA4_EVENT.SAVE_CHANGES, {
      button_name: 'Değişiklikleri Kaydet',
      button_type: 'Save Changes',
      button_area: 'Product Description',
      item_id: productId,
      save_as_template: isReusablePattern.value,
      all_variants_checkbox: applyToVariants.value,
    });

    isSaved.value = true;
    if (description) internalContent.value = description;
    resetTemplateData();
    loadDescriptionTemplates();
    emit('saved');

  } catch (error) {
    console.error('Açıklama kaydedilirken hata oluştu:', error);
    throw error;
  }
};

const getRichText = () => {
  let isValid = true;
  const description = editorContent.value || internalContent.value || "";
  
  if (richText.value?.getRichText) {
    const richTextValidation = richText.value.getRichText();
    isValid = richTextValidation.isValid;
  } else {
    const cleaned = description.replace(/<[^>]*>/g, '').trim();
    isValid = cleaned !== '';
  }
  
  return {
    data: {
      description: description || null,
      templateName: isReusablePattern.value && templateName.value?.trim() 
        ? templateName.value 
        : null,
    },
    isValid,
  };
};

defineExpose({
  handleSaveChanges,
  getRichText,
  resetDirtyState: (opts = {}) => {
    if (typeof opts.savedContent === 'string') {
      internalContent.value = opts.savedContent;
      editorContent.value = opts.savedContent;
    } else if (opts.dropEditorContent) {
      editorContent.value = null;
    }

    isDirty.value = false;
    isDescriptionReadyToSave.value = false;
    showSuccessState.value = Boolean(opts.showSuccessState);
    isSaved.value = false;
    resetTemplateData();
  },
});

const handleRequestUpdate = () => {
  emit("requestUpdate");
};

const openRequestUpdateModal = () => {
  if (isProductDisabled.value) return;
  
  sendProductDetailEvent(GA4_EVENT.BUTTON_CLICK, {
    button_name: 'Güncelleme Talep Et / Hata Bildir',
    button_type: 'Request Update / Report a Bug',
    button_area: 'Product Description',
    item_id: productDetail.value?.id,
  });
  
  requestUpdateModalRef.value?.openModal();
};

const loadDescriptionTemplates = async (skipLoading = false) => {
  const categoryId = productDetail.value?.categoryId;
  if (!categoryId) { return; }

  try {
    const response = await store.dispatch(
      'products/getProductDescription',
      { categoryId, skipLoading }
    );
    templates.value = (response.productDescriptionDtos ?? []).map((item, index) => ({
      id: `template-${index}`,
      name: item.templateName,
      description: (item.description ?? '')
        .replace(/\\r\\n/g, '<br />')
        .replace(/\\n/g, '<br />'),
    }));
  } catch (error) {
    console.error('Açıklama şablonları alınamadı', error);
    templates.value = [];
  }
};

onMounted(loadDescriptionTemplates);
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/utilities.scss";

.info-message {
  display: flex;
  gap: 8px;
  padding-top: 16px;

  .info-text {
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.05px;
    color: #242424;

    .highlight-text {
      color:$colorM50;
      font-weight: 600;
    }
    cursor: pointer;
  }

  &.disabled {
    .info-text {
      color: $colorN60;
      .highlight-text {
        color: $colorN50;
      }
    }
  }
}

.description-content {
  max-height: 300px;
  min-height: 120px;
  overflow-y: auto;
  padding: 12px;
  border: 1px solid $gray-19;
  border-radius: 8px;
  background-color: $colorN00;
  font-size: 14px;
  line-height: 1.6;
  color: $colorN80;
  position: relative;

  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  // Custom scrollbar
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }

  .placeholder {
    color: #999;
    font-style: italic;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 96px;
  }
}

:deep(.section.disabled) {
  .apply-variants {
    pointer-events: none;
    opacity: 0.6;

    input[type="checkbox"] {
      opacity: 0.6;
      cursor: not-allowed;
    }

    label {
      opacity: 0.6;
      cursor: not-allowed;
      color: $colorN60;
    }
  }

  .actions-row {
    button {
      opacity: 0.6;
      cursor: not-allowed;
      pointer-events: none;
    }
  }
}
</style>
